import React from 'react';

export type UsageMeterTone = 'ok' | 'warn' | 'crit';

interface UsageMeterProps {
  used: number;
  /** null = unlimited: the bar is hidden and only the label renders. */
  limit: number | null;
  tone?: UsageMeterTone;
  /** Rendered left/right under the bar. */
  leftLabel?: React.ReactNode;
  rightLabel?: React.ReactNode;
  className?: string;
  /** Bar height class, default h-2. */
  heightClass?: string;
}

const FILL: Record<UsageMeterTone, string> = {
  ok: 'bg-emerald-600',
  warn: 'bg-amber-500',
  crit: 'bg-rose-500',
};

export function usagePercent(used: number, limit: number | null): number {
  if (limit === null) return 0;
  if (limit <= 0) return used > 0 ? 100 : 0;
  return Math.min(100, Math.round((used / limit) * 100));
}

/** "X of Y" bar shared by the upload widget, the bank stats bar, and the license cards. */
export const UsageMeter: React.FC<UsageMeterProps> = ({
  used,
  limit,
  tone = 'ok',
  leftLabel,
  rightLabel,
  className = '',
  heightClass = 'h-2',
}) => {
  const percent = usagePercent(used, limit);
  const unlimited = limit === null;
  return (
    <div className={className} data-testid='usage-meter' data-tone={tone}>
      {!unlimited && (
        <div
          className={`${heightClass} w-full overflow-hidden rounded-full bg-slate-200/80`}
          role='progressbar'
          aria-valuemin={0}
          aria-valuemax={limit}
          aria-valuenow={Math.min(used, limit)}
        >
          <div
            className={`h-full ${FILL[tone]} transition-all duration-300 rounded-full`}
            style={{ width: `${percent}%` }}
          />
        </div>
      )}
      {(leftLabel || rightLabel) && (
        <div className='flex items-center justify-between text-[11px] font-semibold text-slate-500 mt-1.5 tabular-nums'>
          <span>{leftLabel}</span>
          <span>{rightLabel}</span>
        </div>
      )}
    </div>
  );
};
